/**
 * Reason-aware pending-input UI. Renders the appropriate form for each pause
 * reason based on the shared payload contracts in @agentflow/shared.
 *
 * The approval form supports four user actions:
 *   - Approve: accept the plan as-is.
 *   - Request Changes: enter narrative adjustments; the API merges them into
 *     the existing plan via Haiku before resuming to execute.
 *   - Edit Manually: the plan card in the Timeline above becomes a textarea
 *     bound to controlled state in the parent (TaskDetailPage). Pressing
 *     "Save & approve" submits the textarea contents verbatim as the plan.
 *   - Reject: send the plan back for re-planning with the user's notes.
 *
 * For the "Edit Manually" flow the form itself does not own the textarea;
 * the parent passes a controlled `approval` prop so that the in-place editor
 * lives in the Timeline plan card instead of the form. Keeps the user's eyes
 * on the actual plan content while editing.
 *
 * Adding a new pause reason: add a case here and the corresponding type in
 * @agentflow/shared/pauses.ts.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import type { PendingInputRecord } from '@agentflow/shared';

export type ApprovalMode = 'idle' | 'editing' | 'request_changes';

export interface ApprovalDraftState {
  mode: ApprovalMode;
  editedPlan: string;
  requestedChanges: string;
}

export const initialApprovalDraft = (proposedPlan: string): ApprovalDraftState => ({
  mode: 'idle',
  editedPlan: proposedPlan,
  requestedChanges: '',
});

interface Props {
  taskId: string;
  pending: PendingInputRecord;
  onResolved: () => void;
  /**
   * Controlled approval-form state. Required when `pending.reason === 'approval'`
   * so the in-place plan editor (rendered in the Timeline) can stay in sync
   * with the form's mode buttons.
   */
  approval?: {
    state: ApprovalDraftState;
    onStateChange: (next: ApprovalDraftState) => void;
  };
}

export function PendingInputPanel({
  taskId,
  pending,
  onResolved,
  approval,
}: Props): JSX.Element {
  const resolve = useMutation({
    mutationFn: (resolution: Record<string, unknown>) =>
      api.resolvePause(taskId, pending.reason, resolution),
    onSuccess: onResolved,
  });

  return (
    <Card className="border-amber-300 bg-amber-50/40">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Badge variant="warning">paused · {pending.reason}</Badge>
          <span>Awaiting your input</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {pending.reason === 'approval' && approval && (
          // Keying on `pending.id` re-mounts the form when a new approval
          // pause appears (e.g. after `request_changes` cycles back into
          // approval). That clears the form's local `notes` state so it
          // doesn't bleed across iterations.
          <ApprovalForm
            key={pending.id}
            payload={pending.payload}
            state={approval.state}
            onStateChange={approval.onStateChange}
            disabled={resolve.isPending}
            onSubmit={(r) => resolve.mutate(r)}
          />
        )}
        {pending.reason === 'review' && (
          <ReviewForm payload={pending.payload} onSubmit={(r) => resolve.mutate(r)} />
        )}
        {pending.reason === 'clarification' && (
          <ClarificationForm payload={pending.payload} onSubmit={(r) => resolve.mutate(r)} />
        )}
        {pending.reason === 'guardrail' && (
          <GuardrailForm payload={pending.payload} onSubmit={(r) => resolve.mutate(r)} />
        )}
        {resolve.isError && (
          <p className="text-sm text-destructive">Failed to resolve: {String(resolve.error)}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface FormProps<T> {
  payload: Record<string, unknown>;
  onSubmit: (resolution: T) => void;
}

interface ApprovalFormProps {
  payload: Record<string, unknown>;
  state: ApprovalDraftState;
  onStateChange: (next: ApprovalDraftState) => void;
  onSubmit: (resolution: Record<string, unknown>) => void;
  disabled?: boolean;
}

function ApprovalForm({
  payload,
  state,
  onStateChange,
  onSubmit,
  disabled,
}: ApprovalFormProps): JSX.Element {
  const [notes, setNotes] = useState('');
  const proposedPlan = String(payload.proposed_plan ?? '');

  const setMode = (mode: ApprovalMode) => onStateChange({ ...state, mode });

  if (state.mode === 'editing') {
    const dirty = state.editedPlan.trim().length > 0 && state.editedPlan !== proposedPlan;
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Edit the plan above, then save to use your version. Cancel to leave it unchanged.
        </p>
        <div>
          <Label htmlFor="notes" className="text-xs text-muted-foreground">
            Notes (optional)
          </Label>
          <Textarea
            id="notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() =>
              onSubmit({ decision: 'edit', edited_plan: state.editedPlan, notes })
            }
            disabled={disabled || !dirty}
          >
            Save &amp; approve
          </Button>
          <Button
            variant="outline"
            onClick={() => onStateChange({ ...state, mode: 'idle', editedPlan: proposedPlan })}
            disabled={disabled}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (state.mode === 'request_changes') {
    return (
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-muted-foreground">Requested changes</Label>
          <p className="text-xs text-muted-foreground italic mb-1">
            Describe the adjustments you&apos;d like. I&apos;ll merge them into the plan above
            before continuing.
          </p>
          <Textarea
            rows={4}
            value={state.requestedChanges}
            onChange={(e) =>
              onStateChange({ ...state, requestedChanges: e.target.value })
            }
            placeholder="e.g. add a step to validate against the staging database before deploying"
          />
        </div>
        <div>
          <Label htmlFor="notes" className="text-xs text-muted-foreground">
            Notes (optional)
          </Label>
          <Textarea
            id="notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() =>
              onSubmit({
                decision: 'request_changes',
                requested_changes: state.requestedChanges,
                notes,
              })
            }
            disabled={disabled || !state.requestedChanges.trim()}
          >
            Submit changes
          </Button>
          <Button variant="outline" onClick={() => setMode('idle')} disabled={disabled}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // idle — the four primary actions
  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="notes" className="text-xs text-muted-foreground">
          Notes (optional)
        </Label>
        <Textarea
          id="notes"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => onSubmit({ decision: 'approve', notes })}
          disabled={disabled}
        >
          Approve
        </Button>
        <Button variant="outline" onClick={() => setMode('request_changes')} disabled={disabled}>
          Request Changes
        </Button>
        <Button variant="outline" onClick={() => setMode('editing')} disabled={disabled}>
          Edit Manually
        </Button>
        <Button
          variant="destructive"
          onClick={() => onSubmit({ decision: 'reject', notes })}
          disabled={disabled}
        >
          Reject (back to plan)
        </Button>
      </div>
    </div>
  );
}

function ReviewForm({ payload, onSubmit }: FormProps<Record<string, unknown>>): JSX.Element {
  const [feedback, setFeedback] = useState('');

  return (
    <div className="space-y-3">
      {Boolean(payload.output_summary) && (
        <div>
          <Label className="text-xs text-muted-foreground">Output summary</Label>
          <pre className="mt-1 text-xs bg-muted/50 p-2 rounded">
            {String(payload.output_summary)}
          </pre>
        </div>
      )}
      <div>
        <Label className="text-xs text-muted-foreground">
          Feedback (required if requesting changes)
        </Label>
        <Textarea rows={3} value={feedback} onChange={(e) => setFeedback(e.target.value)} />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => onSubmit({ decision: 'accept' })}>Accept output</Button>
        <Button
          variant="outline"
          onClick={() => onSubmit({ decision: 'request_changes', feedback })}
          disabled={!feedback.trim()}
        >
          Request changes (back to execute)
        </Button>
      </div>
    </div>
  );
}

function ClarificationForm({ payload, onSubmit }: FormProps<Record<string, unknown>>): JSX.Element {
  const [answer, setAnswer] = useState('');
  const reason = typeof payload.reason === 'string' ? payload.reason : '';

  return (
    <div className="space-y-3">
      {reason && (
        <div>
          <Label className="text-xs text-muted-foreground">Why I&apos;m asking</Label>
          <p className="text-sm text-muted-foreground italic">{reason}</p>
        </div>
      )}
      <div>
        <Label className="text-xs text-muted-foreground">Question</Label>
        <p className="text-sm">{String(payload.question ?? '')}</p>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Your answer</Label>
        <Textarea rows={3} value={answer} onChange={(e) => setAnswer(e.target.value)} />
      </div>
      <Button onClick={() => onSubmit({ answer })} disabled={!answer.trim()}>
        Send answer
      </Button>
    </div>
  );
}

function GuardrailForm({ payload, onSubmit }: FormProps<Record<string, unknown>>): JSX.Element {
  const [notes, setNotes] = useState('');

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs text-muted-foreground">Risk summary</Label>
        <p className="text-sm">{String(payload.risk_summary ?? '')}</p>
      </div>
      {Boolean(payload.proposed_action) && (
        <div>
          <Label className="text-xs text-muted-foreground">Proposed action</Label>
          <pre className="mt-1 text-xs bg-muted/50 p-2 rounded overflow-x-auto">
            {JSON.stringify(payload.proposed_action, null, 2)}
          </pre>
        </div>
      )}
      <div>
        <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => onSubmit({ decision: 'approve', notes })}>Approve action</Button>
        <Button variant="destructive" onClick={() => onSubmit({ decision: 'deny', notes })}>
          Deny
        </Button>
      </div>
    </div>
  );
}
